import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import type { SkuDTO } from '@invenco/products-interface';

import { DialogBody, DialogDescription, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { TextFormField } from '@/components/forms/text-form-field';
import type { Result } from '@/shared/helpers/Result';

const hsCodeRegex = /^\d{4}\.?\d{2}(?:\.?\d{2}(?:\d{2,4})?)?$/;
const countryCodeRegex = /^[a-zA-Z]{2}$/;

const schema = z.object({
  description: z.string().min(1, 'Enter a description'),
  barcode: z.string().min(1, 'Enter a barcode'),
  hsCode: z
    .string()
    .trim()
    .optional()
    .refine((value) => !value || hsCodeRegex.test(value), 'Enter a valid HS code'),
  countryOfOrigin: z
    .string()
    .trim()
    .optional()
    .refine((value) => !value || countryCodeRegex.test(value), 'Enter a 2-letter country code'),
  customDescription: z.string().trim().optional(),
  usHsCode: z
    .string()
    .trim()
    .optional()
    .refine((value) => !value || hsCodeRegex.test(value), 'Enter a valid HS code'),
  manufacturerIdentificationCode: z.string().trim().optional(),
});

type Props = {
  sku?: Partial<SkuDTO>;
  onSubmit: (data: Partial<SkuDTO>) => Promise<Result> | Promise<void> | void;
};

export function DetailsDialogContent({ sku, onSubmit }: Props) {
  const defaultValues = useMemo(
    () => ({
      description: sku?.description ?? '',
      barcode: sku?.barcode ?? '',
      hsCode: sku?.hsCode ?? '',
      countryOfOrigin: sku?.countryOfOrigin ?? '',
      customDescription: sku?.customDescription ?? '',
      usHsCode: sku?.perCountryHsCodes?.find((code) => code.countryCode === 'US')?.hsCode ?? '',
      manufacturerIdentificationCode: sku?.manufacturerIdentificationCode ?? '',
    }),
    [
      sku?.barcode,
      sku?.countryOfOrigin,
      sku?.customDescription,
      sku?.description,
      sku?.hsCode,
      sku?.manufacturerIdentificationCode,
      sku?.perCountryHsCodes,
    ],
  );

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  return (
    <FormDialogContent
      form={form}
      onSubmit={(data) => {
        const perCountryHsCodes = (sku?.perCountryHsCodes ?? []).filter(
          (code) => code.countryCode !== 'US',
        );

        if (data.usHsCode) {
          perCountryHsCodes.push({
            countryCode: 'US',
            hsCode: data.usHsCode,
          });
        }

        return onSubmit({
          description: data.description,
          barcode: data.barcode,
          hsCode: data.hsCode || null,
          countryOfOrigin: data.countryOfOrigin ? data.countryOfOrigin.toUpperCase() : null,
          customDescription: data.customDescription || null,
          manufacturerIdentificationCode: data.manufacturerIdentificationCode || null,
          perCountryHsCodes: perCountryHsCodes.length ? perCountryHsCodes : null,
        });
      }}
    >
      <DialogHeader>
        <DialogTitle>Edit details</DialogTitle>
        <DialogDescription>Manage core SKU and customs details.</DialogDescription>
      </DialogHeader>
      <DialogBody>
        <TextFormField control={form.control} name="description" label="Description" />
        <TextFormField control={form.control} name="barcode" label="Barcode" />
        <TextFormField control={form.control} name="hsCode" label="HS code" />
        <TextFormField
          control={form.control}
          name="countryOfOrigin"
          label="Country of origin"
          maxLength={2}
        />
        <TextFormField
          control={form.control}
          name="customDescription"
          label="Customs description"
        />
        <TextFormField control={form.control} name="usHsCode" label="US HS code" />
        <TextFormField
          control={form.control}
          name="manufacturerIdentificationCode"
          label="Manufacturer identification code"
        />
      </DialogBody>
    </FormDialogContent>
  );
}
